import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { submitContactForm } from '../../services/publicWrites';
import './PublicPages.css';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', company: '', service: '', message: '' });
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    setSending(true);
    try {
      await submitContactForm({
        name: form.name,
        email: form.email,
        company: form.company,
        service: form.service,
        message: form.message,
      });
      setSent(true);
      setForm({ name: '', email: '', company: '', service: '', message: '' });
      setTimeout(() => setSent(false), 4000);
    } catch (e2) {
      setErr(e2.message || 'Could not send. Check Firebase rules and your connection.');
    }
    setSending(false);
  };

  return (
    <div className="contact-page">
      <div style={{ padding: '120px 24px 60px', textAlign: 'center', background: 'var(--surface-color)', borderBottom: '1px solid var(--border-color)' }}>
        <div className="section-tag">Get In Touch</div>
        <h1 className="section-heading">Contact Us</h1>
        <p style={{ color: 'var(--text-muted)', maxWidth: 480, margin: '0 auto', fontSize: '1rem' }}>
          Ready to start your project? Have questions? We'd love to hear from you.
        </p>
      </div>

      <div className="contact-grid">
        <div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', fontWeight: 700, marginBottom: 32 }}>
            Let's Build Something Together
          </h2>

          <div className="contact-info-item">
            <div className="contact-info-icon"><Mail size={18} /></div>
            <div>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Email</p>
              <p style={{ fontWeight: 600 }}>admin@fitss.co.za</p>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>We reply within 24 hours</p>
            </div>
          </div>
          <div className="contact-info-item">
            <div className="contact-info-icon"><Phone size={18} /></div>
            <div>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Phone</p>
              <p style={{ fontWeight: 600 }}>+27 11 000 0000</p>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Mon–Fri, 8am–5pm SAST</p>
            </div>
          </div>
          <div className="contact-info-item">
            <div className="contact-info-icon"><MapPin size={18} /></div>
            <div>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Office</p>
              <p style={{ fontWeight: 600 }}>123 Tech Street, Sandton</p>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Johannesburg, 2196</p>
            </div>
          </div>

          <div style={{ marginTop: 32, padding: '20px 24px', background: 'var(--surface-color)', border: '1px solid var(--border-accent)', borderRadius: 'var(--radius-xl)' }}>
            <p style={{ fontSize: '0.875rem', color: 'var(--secondary-color)', fontWeight: 700, marginBottom: 6 }}>Free Project Consultation</p>
            <p style={{ fontSize: '0.825rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>Book a free 30-minute discovery call to discuss your project scope, timeline, and budget.</p>
          </div>
        </div>

        <div className="card" style={{ padding: 36 }}>
          {sent ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <CheckCircle size={48} style={{ color: 'var(--success)', marginBottom: 16 }} />
              <h3 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: 8 }}>Message Sent!</h3>
              <p style={{ color: 'var(--text-muted)' }}>We'll get back to you within 24 hours.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="contact-form">
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 700, marginBottom: 4 }}>Send Us a Message</h3>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 16 }}>Saved to Firestore (contacts collection).</p>
              {err && <p style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: 12 }}>{err}</p>}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input className="form-input" placeholder="John Smith" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input className="form-input" type="email" placeholder="john@company.co.za" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Company</label>
                <input className="form-input" placeholder="Your company name" value={form.company} onChange={e => setForm({...form, company: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Service Interested In</label>
                <select className="form-input" value={form.service} onChange={e => setForm({...form, service: e.target.value})}>
                  <option value="">Select a service...</option>
                  <option>Software Development</option>
                  <option>Web Development</option>
                  <option>System Maintenance</option>
                  <option>Graphic Design</option>
                  <option>Multiple Services</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Message *</label>
                <textarea className="form-input" rows={5} placeholder="Tell us about your project, goals, and any specific requirements..." value={form.message} onChange={e => setForm({...form, message: e.target.value})} required />
              </div>
              <button type="submit" className="btn-gold" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: 14 }} disabled={sending}>
                <Send size={16} /> {sending ? 'Sending…' : 'Send Message'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
